import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, CaretRight } from "@phosphor-icons/react";

/**
 * MyTeamWidget — Track A § A10
 * Visible only to users with direct reports in the org hierarchy (PM, HK, UK, AM).
 * Shows each direct report's open case count + escalated count + stale (>48h) count.
 */
export default function MyTeamWidget() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    (async () => {
      try {
        // 1. Get my direct reports
        const { data: dr } = await api.get(`/users/${user.user_id}/direct-reports`);
        if (cancelled) return;
        setReports(dr || []);
        if (!dr?.length) { setLoading(false); return; }
        // 2. For each direct report, fetch their open + escalated case counts (best-effort)
        const { data: cases } = await api.get("/cases", { params: { status: "in_progress" } });
        const { data: esc } = await api.get("/escalations").catch(() => ({ data: [] }));
        const now = Date.now();
        const STALE_MS = 48 * 60 * 60 * 1000;
        const map = {};
        for (const r of dr) {
          const own = (cases || []).filter(c => c.assigned_to === r.user_id || c.assignee_id === r.user_id || c.created_by === r.user_id);
          const escCount = (esc || []).filter(e => own.some(c => c.case_id === e.case_id)).length;
          const stale = own.filter(c => {
            const ts = new Date(c.updated_at || c.created_at).getTime();
            return now - ts > STALE_MS;
          }).length;
          map[r.user_id] = { open: own.length, escalated: escCount, stale };
        }
        if (!cancelled) setStats(map);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (loading) return null;
  if (!reports.length) return null;

  return (
    <div className="card-flat" data-testid="my-team-widget">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--span-border)]">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-[var(--span-text-2)]" />
          <h3 className="font-display font-bold text-[13px] tracking-wide">MY TEAM</h3>
          <span className="text-[10px] text-[var(--span-text-2)] font-mono">· {reports.length} direct reports</span>
        </div>
        <Link to="/settings/team" className="text-[11px] text-[var(--span-text-2)] hover:text-white flex items-center gap-1" data-testid="my-team-view-all">
          View directory <CaretRight size={11} />
        </Link>
      </div>
      <div className="divide-y divide-[var(--span-border)]">
        {reports.map(r => {
          const s = stats[r.user_id] || { open: 0, escalated: 0, stale: 0 };
          const danger = s.escalated > 0;
          const warn = s.stale > 0 && !danger;
          return (
            <Link
              key={r.user_id}
              to={`/cases?assignee=${r.user_id}`}
              data-testid={`my-team-row-${r.initials}`}
              className="grid grid-cols-12 gap-3 items-center px-5 py-3 hover:bg-white/[0.03] transition-colors"
            >
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 flex items-center justify-center font-mono text-[10px] font-bold bg-white/5 border border-[var(--span-border)] shrink-0">
                  {r.initials}
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] font-medium truncate flex items-center gap-1.5">
                    <span className="truncate">{r.name}</span>
                    {r.rm_md === "RM" && <span className="font-mono text-[7px] tracking-[0.18em] px-1 py-px bg-sky-500/15 text-sky-300 border border-sky-500/30">RM</span>}
                    {r.rm_md === "MD" && <span className="font-mono text-[7px] tracking-[0.18em] px-1 py-px bg-amber-500/15 text-amber-300 border border-amber-500/30">MD</span>}
                  </div>
                  <div className="text-[10px] text-[var(--span-text-2)] truncate">{r.role}</div>
                </div>
              </div>
              <Stat label="Open" value={s.open} />
              <Stat label="Escalated" value={s.escalated} tone={s.escalated > 0 ? "danger" : "neutral"} />
              <Stat label="Stale >48h" value={s.stale} tone={warn ? "warn" : "neutral"} />
              <div className="col-span-1 flex justify-end">
                {danger && <span className="w-2 h-2 rounded-full bg-red-500 inline-block" data-testid={`indicator-danger-${r.initials}`} />}
                {!danger && warn && <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" data-testid={`indicator-warn-${r.initials}`} />}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const tc = tone === "danger" ? "text-red-400" : tone === "warn" ? "text-amber-400" : "text-[var(--span-text)]";
  return (
    <div className="col-span-2 text-center">
      <div className={`font-mono font-bold text-[13px] ${tc}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[var(--span-text-2)]/70">{label}</div>
    </div>
  );
}
